import { Card, Flex, SimpleTable, Typography } from "djuno-design";
import { ReactComponent as FaceSmile } from "./../assets/icons/face-smile.svg";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text, Paragraph, Link } = Typography;

const TypographyPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Typography Component Overview</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Typography component in Djuno Design provides a flexible and
            highly customizable text rendering solution. It supports a variety
            of elements, such as titles, paragraphs, links, and inline text, and
            offers options for styling with bold, italic, underline, code, and
            more. Additionally, it includes functionality for copyable text and
            tooltips, allowing for a dynamic and interactive user experience.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Typography component supports different UI types to adapt the
            text’s appearance, such as:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Secondary
                </Text>
                : For supplementary information.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Success
                </Text>
                : Indicating successful operations.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Warning
                </Text>
                : Highlighting important warnings.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Danger
                </Text>
                : For error messages or alerts.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Disabled
                </Text>
                : Displaying inactive or unavailable content.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Transparent
                </Text>
                : Used to render invisible or transparent text.
              </Text>
            </li>
          </ul>
          <Text size="sm" className="mt-4">
            In addition, Typography provides flexible{" "}
            <Text size="sm" strong>
              size
            </Text>{" "}
            options, ranging from extra-small (xs) to extra-large (9xl), and{" "}
            <Text size="sm" strong>
              level
            </Text>{" "}
            options (h1 to h6) for titles. You can also add specific styles like{" "}
            <Text size="sm" strong>
              code
            </Text>
            ,{" "}
            <Text size="sm" strong>
              mark
            </Text>
            ,{" "}
            <Text size="sm" strong>
              underline
            </Text>
            ,{" "}
            <Text size="sm" strong>
              del
            </Text>
            ,{" "}
            <Text size="sm" strong>
              strong
            </Text>
            , and{" "}
            <Text size="sm" strong>
              italic
            </Text>
            .
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              title="Typography"
              description="different types of heading"
              codeViewerProps={{
                code: TypographyExample1,
              }}
            >
              <Title>h1. Djuno Design</Title>
              <Title level={2}>h2. Djuno Design</Title>
              <Title level={3}>h3. Djuno Design</Title>
              <Title level={4}>h4. Djuno Design</Title>
              <Title level={5}>h5. Djuno Design</Title>
              <Title level={6}>h6. Djuno Design</Title>
            </ExpandableCard>
            <ExpandableCard
              title="Typography"
              description="different types of text"
              codeViewerProps={{
                code: TypographyExample2,
              }}
            >
              <Typography>
                <Flex direction="col" className="gap-2">
                  <Text>Djuno Design (default)</Text>
                  <Text uiType="secondary">Djuno Design (secondary)</Text>
                  <Text uiType="success">Djuno Design (success)</Text>
                  <Text uiType="warning">Djuno Design (warning)</Text>
                  <Text uiType="danger">Djuno Design (danger)</Text>
                  <Text uiType="disabled">Djuno Design (disabled)</Text>
                  <Text mark>Djuno Design (mark)</Text>
                  <Text code>Djuno Design (code)</Text>
                  <Text underline>Djuno Design (underline)</Text>
                  <Text del>Djuno Design (delete)</Text>
                  <Text strong>Djuno Design (strong)</Text>
                  <Text italic>Djuno Design (italic)</Text>
                  <Link href="https://google.com" target="_blank">
                    Djuno Design (link)
                  </Link>
                  <Text
                    tooltip={{ content: "I'm a tooltip for <Typography/>" }}
                  >
                    Djuno Design (toottip)
                  </Text>
                  <Paragraph>Djuno Design (paragraph)</Paragraph>
                </Flex>
              </Typography>
            </ExpandableCard>
            <ExpandableCard
              title="Typography"
              description="different types of size"
              codeViewerProps={{
                code: TypographyExample3,
              }}
            >
              <Flex direction="col" className="gap-2">
                <Text size="xs">Djuno Design (xs)</Text>
                <Text size="sm">Djuno Design (sm)</Text>
                <Text size="base">
                  Djuno Design (base)
                  <Text size="xs" uiType="secondary">
                    default
                  </Text>
                </Text>
                <Text size="lg">Djuno Design (lg)</Text>
                <Text size="xl">
                  Djuno Design (xl)
                  <Text size="xs" uiType="secondary">
                    Available until 9xl
                  </Text>
                </Text>
              </Flex>
            </ExpandableCard>
            <ExpandableCard
              title="Typography"
              description="copyable options"
              codeViewerProps={{
                code: TypographyExample4,
              }}
            >
              <Flex direction={"col"} className="gap-3 w-full">
                <Paragraph copyable>This is a copyable text.</Paragraph>
                <Text copyable={{ text: "Hello, Djuno Design!" }}>
                  Replace copy text.
                </Text>
                <Text
                  copyable={{
                    tooltips: ["click here", "you clicked!!"],
                    icons: [
                      <FaceSmile />,
                      <FaceSmile className="text-green-500" />,
                    ],
                  }}
                >
                  Custom Copy icon and replace tooltips text.
                </Text>
                <Text copyable={{ tooltips: false }}>Hide Copy tooltips.</Text>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-5 w-full">
            <Card id="API" description="Common props">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* uiType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the appearance of the text based on its type
                        (e.g., success, danger).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Typography.ts#L25"}
                        size="xs"
                        code
                      >
                        TypographtUiTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">default</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">size</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Flex direction="col" className="gap-1">
                        <Text size="sm">Specifies the size of the text.</Text>
                        <Text
                          code
                          size="xs"
                          className="block whitespace-nowrap"
                        >
                          except Title, it has level
                        </Text>
                      </Flex>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Typography.ts#L26"}
                        size="xs"
                        code
                      >
                        TypographtSizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">base</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tooltip */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tooltip</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Displays a tooltip on hover.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L37"}
                        size="xs"
                        code
                      >
                        TooltipProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* code */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">code</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Renders the text as inline code.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* mark */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">mark</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Highlights the text by marking it with a background
                        color.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* underline */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">underline</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Underlines the text.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* del */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">del</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Strikes through the text.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* strong */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">strong</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Renders the text as bold (strong).</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* italic */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">italic</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        Renders the text in italics.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* copyable */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">copyable</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Makes the text copyable, with optional tooltip and icon
                        configurations for copying behavior.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L72"}
                        size="xs"
                        code
                      >
                        boolean | CopyableOptionsProp
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="Title API" description="<Typography.Title/>">
              <SimpleTable containerClassName="!min-h-14">
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* level */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">level</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Flex direction="col" className="gap-1">
                        <Text size="sm">
                          Defines the heading level for title elements (h1 to
                          h6).
                        </Text>
                        <Text
                          code
                          size="xs"
                          className="block whitespace-nowrap"
                        >
                          1 | 2 | 3 | 4 | 5 | 6
                        </Text>
                      </Flex>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">number</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">1</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
            <Card id="Link API" description="<Typography.Link/>">
              <SimpleTable containerClassName="!min-h-14">
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* level */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">
                        all <Text code size="xs">{`<a>`}</Text> props
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">props like href, target, etc.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">React.AnchorHTMLAttributes</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const TypographyExample1 = `
<Title>h1. Djuno Design</Title>
<Title level={2}>h2. Djuno Design</Title>
<Title level={3}>h3. Djuno Design</Title>
<Title level={4}>h4. Djuno Design</Title>
<Title level={5}>h5. Djuno Design</Title>
<Title level={6}>h6. Djuno Design</Title>`;
const TypographyExample2 = `<Typography>
  <Flex direction="col" className="gap-2">
    <Text>Djuno Design (default)</Text>
    <Text uiType="secondary">Djuno Design (secondary)</Text>
    <Text uiType="success">Djuno Design (success)</Text>
    <Text uiType="warning">Djuno Design (warning)</Text>
    <Text uiType="danger">Djuno Design (danger)</Text>
    <Text uiType="disabled">Djuno Design (disabled)</Text>
    <Text mark>Djuno Design (mark)</Text>
    <Text code>Djuno Design (code)</Text>
    <Text underline>Djuno Design (underline)</Text>
    <Text del>Djuno Design (delete)</Text>
    <Text strong>Djuno Design (strong)</Text>
    <Text italic>Djuno Design (italic)</Text>
    <Link href="https://google.com" target="_blank">
      Djuno Design (link)
    </Link>
    <Text
      tooltip={{ content: "I'm a tooltip for <Typography/>" }}
    >
      Djuno Design (toottip)
    </Text>
    <Paragraph>Djuno Design (paragraph)</Paragraph>
  </Flex>
</Typography>`;
const TypographyExample3 = `<Flex direction="col" className="gap-2">
  <Text size="xs">Djuno Design (xs)</Text>
  <Text size="sm">Djuno Design (sm)</Text>
  <Text size="base">
    Djuno Design (base)
    <Text size="xs" uiType="secondary">
      default
    </Text>
  </Text>
  <Text size="lg">Djuno Design (lg)</Text>
  <Text size="xl">
    Djuno Design (xl)
    <Text size="xs" uiType="secondary">
      Available until 9xl
    </Text>
  </Text>
  </Flex>`;
const TypographyExample4 = `<Flex direction={"col"} className="gap-3 w-full">
  <Paragraph copyable>This is a copyable text.</Paragraph>
  <Text copyable={{ text: "Hello, Djuno Design!" }}>
    Replace copy text.
  </Text>
  <Text
    copyable={{
      tooltips: ["click here", "you clicked!!"],
      icon: [
        <FaceSmile />,
        <FaceSmile className="text-green-500" />,
      ],
    }}
  >
    Custom Copy icon and replace tooltips text.
  </Text>
  <Text copyable={{ tooltips: false }}>Hide Copy tooltips.</Text>
</Flex>`;

export default TypographyPage;
